/**
 * crm/LeadDetailDrawer.jsx — Phase 75 refactor
 * Extracted from CRMShell.jsx (Phase 74). Right-side slide-over with every
 * Lead field. Inline editing PATCHes immediately + keeps state in sync via
 * the `onPatch` callback.
 */
import React from "react";
import { X } from "lucide-react";
import {
  InlineText, daysAgo,
  LEAD_STAGES, LEAD_STATUSES, LEAD_TYPES, SERVICES, ICP_OPTIONS,
  useLeadStages,
} from "./_shared";

export function LeadDetailDrawer({ lead, canEdit, onClose, onPatch }) {
  const { stages } = useLeadStages();
  const activeStages = stages && stages.length ? stages : LEAD_STAGES;
  const age = daysAgo(lead.created_at);
  const row = (label, child) => (
    <div className="grid grid-cols-3 gap-2 items-start py-1.5 border-b border-slate-100 last:border-0">
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="col-span-2 text-sm text-slate-900">{child}</div>
    </div>
  );
  const selectField = (field, options, testId) => (
    <select
      value={lead[field] || ""}
      onChange={(e) => onPatch(field, e.target.value || null)}
      disabled={!canEdit}
      className="bg-transparent border-0 w-full text-sm py-0.5 focus:bg-white focus:ring-1 focus:ring-[#F54800] focus:rounded-sm outline-none disabled:text-slate-400"
      data-testid={testId}
    >
      <option value="">—</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose} data-testid="crm-lead-drawer">
      <div className="bg-white w-full max-w-md h-full overflow-y-auto shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between">
          <div className="min-w-0">
            {lead.code && <div className="text-[9px] font-mono uppercase tracking-wider text-slate-400">{lead.code}</div>}
            <div className="font-display text-lg font-bold text-slate-900 truncate">{lead.company_name || "(no company)"}</div>
            <div className="text-xs text-slate-500 truncate">{lead.contact_name || "—"}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 ml-2 shrink-0" data-testid="crm-lead-drawer-close"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-1">
          {row("Lead ID", <span className="font-mono text-xs text-slate-700">{lead.code || lead.id?.slice(0, 8)}</span>)}
          {row("Lead Date", lead.lead_date || (lead.created_at || "").slice(0, 10))}
          {row("Lead Age", age !== null ? <span className={age > 14 ? "text-rose-600" : age > 7 ? "text-amber-600" : "text-slate-700"}>{age} days</span> : "—")}
          {row("Client (Company)", <InlineText value={lead.company_name || ""} onSave={(v) => onPatch("company_name", v)} placeholder="Company" />)}
          {row("Lead Name (Contact)", <InlineText value={lead.contact_name || ""} onSave={(v) => onPatch("contact_name", v)} placeholder="Contact name" />)}
          {row("Email", <InlineText value={lead.email || ""} onSave={(v) => onPatch("email", v || null)} placeholder="email@…" type="email" />)}
          {row("Phone", <InlineText value={lead.phone || ""} onSave={(v) => onPatch("phone", v || null)} placeholder="+91…" />)}
          {row("Source", selectField("source", ["Website", "Referral", "Cold Outreach", "LinkedIn", "Conference", "Partner", "Inbound Call", "Prospect Conversion", "Public Intake"], `crm-lead-drawer-source`))}
          {row("Lead Type", selectField("lead_type", LEAD_TYPES, `crm-lead-drawer-type`))}
          {row("Service", selectField("service", SERVICES, `crm-lead-drawer-service`))}
          {row("ICP", (
            <select
              value={lead.icp || ""}
              onChange={(e) => onPatch("icp", e.target.value || null)}
              disabled={!canEdit}
              className={`bg-transparent border-0 w-full text-sm py-0.5 focus:bg-white focus:ring-1 focus:ring-[#F54800] focus:rounded-sm outline-none disabled:text-slate-400 ${lead.icp === "Student" ? "text-rose-600 font-medium" : ""}`}
              data-testid="crm-lead-drawer-icp"
            >
              <option value="">—</option>
              {ICP_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ))}
          {row("Current Stage", selectField("stage", activeStages, `crm-lead-drawer-stage`))}
          {row("Lead Status", selectField("lead_status", LEAD_STATUSES, `crm-lead-drawer-status`))}
          {row("Lead Closed Date", <InlineText value={lead.lead_closed_date || ""} onSave={(v) => onPatch("lead_closed_date", v || null)} placeholder="YYYY-MM-DD" />)}
          {row("Lead Value (₹)", <InlineText value={lead.lead_value ?? ""} onSave={(v) => onPatch("lead_value", v ? Number(v) : null)} placeholder="0" type="number" testId="crm-lead-drawer-value" />)}
          {row("Owner", <InlineText value={lead.owner_id || ""} onSave={(v) => onPatch("owner_id", v || null)} placeholder="user_id" />)}
          {row("Drive Links", <InlineText value={lead.drive_links || ""} onSave={(v) => onPatch("drive_links", v || null)} placeholder="paste link(s)" testId="crm-lead-drawer-drive" />)}
          {row("Country", <InlineText value={lead.country || ""} onSave={(v) => onPatch("country", v || null)} placeholder="—" />)}
          {row("Industry", <InlineText value={lead.industry || ""} onSave={(v) => onPatch("industry", v || null)} placeholder="—" />)}
          {row("Notes", (
            <textarea
              defaultValue={lead.notes || ""}
              onBlur={(e) => e.target.value !== (lead.notes || "") && onPatch("notes", e.target.value || null)}
              disabled={!canEdit}
              placeholder="Add notes…"
              rows={3}
              className="w-full text-sm bg-transparent border border-slate-200 rounded-sm px-2 py-1 focus:bg-white focus:ring-1 focus:ring-[#F54800] outline-none disabled:text-slate-400 disabled:bg-slate-50"
              data-testid="crm-lead-drawer-notes"
            />
          ))}
        </div>
      </div>
    </div>
  );
}
